import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteFolder } from '../api/folders.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';

export interface DeleteFolderParams {
  folderId: string;
  folderTitle?: string;
}

/**
 * Hook to delete a folder using TanStack Query mutation
 *
 * @returns TanStack Query mutation result with delete folder functionality
 */
export const useDeleteFolder = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<undefined, Error, DeleteFolderParams>({
    mutationFn: async ({ folderId }: DeleteFolderParams) => {
      await deleteFolder(folderId);
      return undefined;
    },
    onSuccess: (_, variables) => {
      // Invalidate folders query to refetch the list
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });
      // Also invalidate the specific folder query
      void queryClient.invalidateQueries({ queryKey: ['folder', variables.folderId] });

      const message = variables.folderTitle
        ? t('guidelines.useDeleteFolder.folderDeleted', { title: variables.folderTitle })
        : t('guidelines.useDeleteFolder.folderDeletedSuccessfully');

      notificationService.showSuccess(message, {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      });
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('guidelines.useDeleteFolder.failedToDeleteFolder'),
      );
    },
  });
};
